import { useEffect, useState } from "react";
import { Terminal, Filter } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import { ops } from "@/lib/api";
import { fmtTime, shortId } from "@/lib/format";

const ACTION_COLOR = (action) => {
  if (action.includes("fail") || action.includes("requires_review")) return "rgb(196,52,44)";
  if (action.includes("compliance") || action.includes("review")) return "rgb(176,116,18)";
  if (action.includes("created") || action.includes("captured") || action.includes("signed") || action.includes("scheduled")) return "rgb(32,138,78)";
  return "rgb(36,104,184)";
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ action: "", entity_type: "", actor: "" });

  const load = async () => {
    const params = {};
    if (filter.action) params.action = filter.action;
    if (filter.entity_type) params.entity_type = filter.entity_type;
    if (filter.actor) params.actor = filter.actor;
    const data = await ops.audit(params);
    setRows(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="page-audit" className="p-6 md:p-10">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Immutable Audit Trail</div>
          <h1 className="font-display text-[40px] leading-[1.02] truncate">Audit Log</h1>
        </div>
        <div className="shrink-0">
          <MockBadge />
        </div>
      </div>

      <div className="pj-card mb-4 p-3 flex flex-wrap gap-3 items-end">
        <Field label="Action contains" value={filter.action} onChange={(v) => setFilter({ ...filter, action: v })} testid="audit-filter-action" />
        <Field label="Entity type" value={filter.entity_type} onChange={(v) => setFilter({ ...filter, entity_type: v })} testid="audit-filter-entity" />
        <Field label="Actor ID" value={filter.actor} onChange={(v) => setFilter({ ...filter, actor: v })} testid="audit-filter-actor" />
        <button
          type="button"
          onClick={load}
          data-testid="audit-apply"
          className="mono inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.12em] shrink-0 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
        >
          <Filter size={12} /> Apply
        </button>
      </div>

      <div className="pj-card" style={{ background: "rgb(var(--surface))" }}>
        <div className="px-4 py-3 mono text-[10px] uppercase tracking-[0.18em] text-[rgb(var(--dim))] flex items-center gap-2" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
          <Terminal size={12} /> events · most recent first
        </div>
        <div className="max-h-[68vh] overflow-auto">
          {rows.length === 0 && (
            <div className="px-4 py-10 mono text-[12px] text-[rgb(var(--dim))]">No events match.</div>
          )}
          {rows.map((r) => (
            <div key={r.id} data-testid={`audit-row-${r.id}`} className="px-4 py-2.5 mono text-[11px] flex items-start gap-3 min-w-[860px]" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
              <span className="text-[rgb(var(--muted-2))] shrink-0 w-32 truncate" title={fmtTime(r.created_at)}>{fmtTime(r.created_at)}</span>
              <span className="shrink-0 w-32 text-[rgb(var(--dim))] truncate" title={`${r.actor_type}:${r.actor_id}`}>{r.actor_type}:{shortId(r.actor_id)}</span>
              <span className="shrink-0 w-56 truncate" style={{ color: ACTION_COLOR(r.action) }} title={r.action}>{r.action}</span>
              <span className="shrink-0 w-44 text-[rgb(var(--fg))] truncate" title={`${r.entity_type}:${r.entity_id}`}>{r.entity_type}:{shortId(r.entity_id)}</span>
              <span className="text-[rgb(var(--dim))] truncate min-w-0 flex-1">
                {Object.keys(r.outputs || {}).length ? JSON.stringify(r.outputs) : Object.keys(r.inputs || {}).length ? JSON.stringify(r.inputs) : ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="pj-label block mb-1.5">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-44 mono text-[12px] px-2 py-2"
        style={{ background: "rgb(var(--surface-2))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
      />
    </label>
  );
}
